import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  orderManagerTabForStatus,
  ordersHighlightPath,
} from '../../utils/navigation';
import { decideOrderFaxSignature } from '../../services/orderFaxes';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { ExternalLink, Loader2, Scissors } from 'lucide-react';
import FileToChartDialog from './FileToChartDialog';
import PageEditor from './PageEditor';
import SenderLabel from './SenderLabel';
import TagPicker from './TagPicker';
import PatientOrDraftPicker, {
  type PickerSelection,
} from './PatientOrDraftPicker';
import { draftSummary, patientLabel } from './patientOrDraftLabels';
import { allPagesOf, type BoardPage, type PageBoardSource } from './pageBoard';
import {
  assembleFaxDocument,
  editFaxDocumentPages,
  getFaxDocumentUrl,
  renameFaxDocument,
  splitFaxDocument,
} from '../../services/faxDocuments';
import {
  getReceivedFax,
  getReceivedFaxDocumentUrl,
} from '../../services/faxInbox';
import {
  listFaxTypeOptions,
  listPatientAdmissions,
  setClassification,
  type AdmissionOption,
  type FaxTag,
  type FaxTypeOption,
  type PatientOption,
} from '../../services/faxClassification';
import { renameFaxItem, type FaxItem } from '../../services/faxItems';
import { archiveFaxItem, unarchiveFaxItem } from '../../services/faxRouting';
import {
  createIntakeDraft,
  type IntakeDraft,
} from '../../services/intakeDrafts';
import {
  getBusinessErrorMessage,
  getErrorConflictCode,
} from '../../utils/apiErrorDetail';

const NONE = '__none__';

interface PageEditorDialogProps {
  /** The row being worked on. Null means the dialog is closed. */
  item: FaxItem | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
  onConflict: () => void;
  /**
   * A split happened and the editor should carry on with what is left. The
   * caller supplies the item to continue with, because a document's version
   * moved and an arrival has become a document.
   */
  onSplit: (remaining: FaxItem) => void;
  /** Filed to the chart or archived: it has left the list it was in. */
  onRouted: (message: string) => void;
}

/**
 * One editor for anything in the inbox.
 *
 * There used to be two dialogs, one to build a document out of a transmission
 * and one to re-arrange a document that already existed. They did the same
 * thing to the same pages and differed only in which table the row came from,
 * which is the system's business rather than the reader's.
 *
 * So the difference lives here and nowhere the reader can see. Editing a
 * document re-bakes it. Editing a transmission builds a document out of it and
 * leaves what arrived exactly as it arrived, still on record, because the pages
 * a sender actually transmitted are not ours to rewrite.
 *
 * Pages start in arrival order, oldest first, which is the opposite of the list
 * above. A sender working through a stack faxes page one first, so oldest first
 * is the order they meant; newest first would build a document that reads
 * backwards.
 */
export default function PageEditorDialog({
  item,
  onClose,
  onSaved,
  onError,
  onConflict,
  onSplit,
  onRouted,
}: PageEditorDialogProps) {
  const [sources, setSources] = useState<PageBoardSource[]>([]);
  const [sourceIds, setSourceIds] = useState<string[]>([]);
  const [pages, setPages] = useState<BoardPage[]>([]);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [splitKeys, setSplitKeys] = useState<Set<string>>(new Set());
  const [splitName, setSplitName] = useState('');
  const [types, setTypes] = useState<FaxTypeOption[]>([]);
  const [typeId, setTypeId] = useState<string>(NONE);
  const [tags, setTags] = useState<FaxTag[]>([]);
  // One field, one answer: a fax is about a patient the agency already has or
  // about a referral somebody is still assembling, never both.
  const [selection, setSelection] = useState<PickerSelection | null>(null);
  const [admissions, setAdmissions] = useState<AdmissionOption[]>([]);
  const [admissionId, setAdmissionId] = useState<string>(NONE);
  // What the pages looked like when they loaded. Saving only rebuilds the
  // document when they actually changed, so opening a row to set its type
  // does not quietly bake a new version of it.
  const [originalPages, setOriginalPages] = useState('');
  const [filingOpen, setFilingOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);

  // Restored from the row rather than blanked. Every field here is saved
  // whole on every save, so opening a classified fax with an empty patient
  // and pressing Save would clear a patient nobody meant to clear.
  useEffect(() => {
    setName(item?.name ?? '');
    setTypeId(item?.fax_type_id ?? NONE);
    setTags(item?.tags ?? []);
    setAdmissions([]);
    setAdmissionId(item?.admission_id ?? NONE);
    if (item?.patient_id) {
      setSelection({
        kind: 'patient',
        id: item.patient_id,
        label: item.patient_label ?? 'Patient',
      });
    } else if (item?.intake_draft_id) {
      setSelection({
        kind: 'draft',
        id: item.intake_draft_id,
        label: item.intake_draft_name ?? 'Draft intake',
      });
    } else {
      setSelection(null);
    }
  }, [item]);

  // The admissions a restored patient has, so the one already saved shows as
  // itself rather than as a blank select.
  useEffect(() => {
    if (selection?.kind !== 'patient') {
      setAdmissions([]);
      return;
    }
    let cancelled = false;
    const patientId = selection.id;
    // A patient always has one; only a draft awaiting its save does not.
    if (patientId === null) return;
    void (async () => {
      try {
        const loaded = await listPatientAdmissions(patientId);
        if (!cancelled) setAdmissions(loaded);
      } catch {
        if (!cancelled)
          onError('Could not load that patient\u2019s admissions.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selection, onError]);

  useEffect(() => {
    if (!item) return;
    let cancelled = false;
    void (async () => {
      try {
        const options = await listFaxTypeOptions();
        if (!cancelled) setTypes(options);
      } catch {
        if (!cancelled) onError('Could not load the types.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [item, onError]);

  // A fax can hold a type that has since been retired, and the list of
  // choosable types no longer offers it. Without this the select falls back to
  // None and the next save quietly reclassifies the fax as nothing.
  const typeOptions = useMemo(() => {
    if (!item?.fax_type_id) return types;
    if (types.some(type => type.id === item.fax_type_id)) return types;
    return [
      ...types,
      {
        id: item.fax_type_id,
        name: item.fax_type_name
          ? `${item.fax_type_name} (retired)`
          : 'Retired type',
      } as FaxTypeOption,
    ];
  }, [types, item?.fax_type_id, item?.fax_type_name]);

  /**
   * Answer for the page on screen. The order moves only on a yes, and either
   * answer is recorded against the person who gave it.
   */
  const decideSignature = useCallback(
    async (signed: boolean) => {
      const orderId = item?.answers_order_id;
      if (!orderId) return;
      setDeciding(true);
      try {
        await decideOrderFaxSignature(orderId, signed);
        onSaved();
      } catch {
        onError('Could not record that answer.');
      } finally {
        setDeciding(false);
      }
    },
    [item?.answers_order_id, onSaved, onError]
  );

  const choosePatient = useCallback((chosen: PatientOption) => {
    setSelection({
      kind: 'patient',
      id: chosen.id,
      label: patientLabel(chosen),
    });
    // Cleared first: an admission belongs to one patient, and carrying the
    // last one over would offer an episode of care from somebody else.
    setAdmissionId(NONE);
  }, []);

  const startDraft = useCallback((name: string) => {
    // Named only. The save writes it, together with the link from this fax,
    // so closing without saving leaves nothing behind.
    setSelection({
      kind: 'draft',
      id: null,
      label: name,
      sublabel: 'Will be started when you save',
    });
    setAdmissionId(NONE);
    setAdmissions([]);
  }, []);

  const chooseDraft = useCallback((chosen: IntakeDraft) => {
    setSelection({
      kind: 'draft',
      id: chosen.id,
      label: chosen.name,
      sublabel: draftSummary(chosen),
    });
    // A draft has no admissions: there is no patient yet for one to belong to.
    setAdmissionId(NONE);
    setAdmissions([]);
  }, []);

  const clearSelection = useCallback(() => {
    setSelection(null);
    setAdmissions([]);
    setAdmissionId(NONE);
  }, []);

  useEffect(() => {
    if (!item) {
      setSources([]);
      setSourceIds([]);
      setPages([]);
      return;
    }
    let cancelled = false;
    setPreparing(true);
    setPages([]);
    // A fresh selection per thing opened, or pages ticked on the last one
    // would still be ticked against page keys that no longer exist.
    setSplitKeys(new Set());
    setSplitName('');
    void (async () => {
      try {
        if (item.kind === 'document') {
          const url = await getFaxDocumentUrl(item.id);
          if (cancelled) return;
          setSourceIds([]);
          setSources([{ url, pageCount: null }]);
          return;
        }
        // A transmission can carry several attachments, and only the ones that
        // finished downloading have bytes to show.
        const fax = await getReceivedFax(item.id);
        const ready = fax.documents.filter(
          document => document.status === 'ready'
        );
        const urls = await Promise.all(
          ready.map(document => getReceivedFaxDocumentUrl(fax.id, document.id))
        );
        if (cancelled) return;
        setSourceIds(ready.map(document => document.id));
        setSources(urls.map(url => ({ url, pageCount: null })));
      } catch {
        if (!cancelled) onError('Could not open those pages.');
      } finally {
        if (!cancelled) setPreparing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [item, onError]);

  const onSourceLoaded = useCallback((index: number, pageCount: number) => {
    setSources(current => {
      if (current[index]?.pageCount === pageCount) return current;
      const next = current.map((source, position) =>
        position === index ? { ...source, pageCount } : source
      );
      // Seeded once every source has been read, so the rail starts as the whole
      // fax and the work is taking pages away rather than finding them.
      if (next.every(source => source.pageCount !== null)) {
        setPages(previous => {
          if (previous.length) return previous;
          const seeded = allPagesOf(next);
          setOriginalPages(JSON.stringify(refs(seeded)));
          return seeded;
        });
      }
      return next;
    });
  }, []);

  /**
   * The stored object, in a new tab, scrollable the way a reader wants it.
   *
   * For a transmission that is its first readable attachment, which is what
   * the pages on the rail were built from.
   */
  const openOriginal = useCallback(async () => {
    if (!item) return;
    // Opened before the await, not after it. A window.open that follows an
    // await has lost the user gesture and is what pop-up blockers stop.
    const tab = window.open('', '_blank');
    if (!tab) {
      onError(
        'Your browser blocked the download. Allow pop-ups for this site and try again.'
      );
      return;
    }
    try {
      if (item.kind === 'document') {
        tab.location.href = await getFaxDocumentUrl(item.id);
        return;
      }
      const fax = await getReceivedFax(item.id);
      const ready = fax.documents.find(document => document.status === 'ready');
      if (!ready) {
        tab.close();
        onError('That fax has no pages that can be opened yet.');
        return;
      }
      tab.location.href = await getReceivedFaxDocumentUrl(fax.id, ready.id);
    } catch {
      tab.close();
      onError('Could not open that.');
    }
  }, [item, onError]);

  const toggleSplit = useCallback((key: string) => {
    setSplitKeys(current => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const refs = (chosen: BoardPage[]) =>
    chosen.map(({ source, page, rotation }) => ({ source, page, rotation }));

  const taken = pages.filter(page => splitKeys.has(page.key));
  const kept = pages.filter(page => !splitKeys.has(page.key));
  // A document with no pages is not a document, so taking everything is
  // refused the same way taking nothing is.
  const canSplit =
    taken.length > 0 && kept.length > 0 && splitName.trim() !== '';

  /**
   * Separate the ticked pages into a fax of their own.
   *
   * The pages move rather than being copied, and the editor carries on with
   * what is left, because taking a stapled fax apart is usually more than one
   * cut and this makes the second cut free.
   */
  const split = useCallback(async () => {
    if (!item || !canSplit) return;
    setSaving(true);
    try {
      if (item.kind === 'document') {
        if (item.version === null) {
          onError('That document could not be split. Please reload and retry.');
          return;
        }
        await splitFaxDocument(item.id, {
          name: splitName.trim(),
          version: item.version,
          taken: refs(taken),
          kept: refs(kept),
        });
        // One version on, because the server re-baked what is left. Carrying
        // the old one would have the next save refused against a document
        // this editor itself moved.
        onSplit({ ...item, version: item.version + 1 });
      } else {
        // Nothing exists to split yet, so both halves are built from what
        // arrived and the transmission itself is left exactly as it arrived.
        await assembleFaxDocument({
          name: splitName.trim(),
          sources: sourceIds,
          pages: refs(taken),
        });
        const remainder = await assembleFaxDocument({
          name: name.trim() || splitName.trim(),
          sources: sourceIds,
          pages: refs(kept),
        });
        onSplit({
          kind: 'document',
          id: remainder.id,
          name: remainder.name,
          page_count: remainder.page_count,
          received_at: item.received_at,
          status: 'ready',
          // One arrival's pages, so its sender and order carry over until
          // the reload brings the server's row.
          fax_number_from: item.fax_number_from,
          sender_physician_name: item.sender_physician_name,
          answers_order_id: item.answers_order_id,
          answers_order_status: item.answers_order_status,
          version: remainder.version,
          filed: false,
        });
      }
      setSplitKeys(new Set());
      setSplitName('');
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 409) {
        onConflict();
        onClose();
      } else {
        console.error('Failed to split', error);
        onError('Could not split those pages out.');
      }
    } finally {
      setSaving(false);
    }
  }, [
    canSplit,
    item,
    kept,
    name,
    onClose,
    onConflict,
    onError,
    onSplit,
    sourceIds,
    splitName,
    taken,
  ]);

  /** What the classification fields currently say, as the API takes it. */
  const classification = useCallback(
    (draftId: string | null) => ({
      fax_type_id: typeId === NONE ? null : typeId,
      patient_id: selection?.kind === 'patient' ? selection.id : null,
      admission_id: admissionId === NONE ? null : admissionId,
      intake_draft_id: selection?.kind === 'draft' ? draftId : null,
      tag_ids: tags.map(tag => tag.id),
    }),
    [admissionId, selection, tags, typeId]
  );

  const save = useCallback(async () => {
    if (!item) return;
    const wanted = name.trim();
    if (!wanted) return;
    setSaving(true);
    try {
      // A draft chosen from the list already exists; one started here does
      // not, and is written now rather than when it was picked, so closing
      // without saving cannot leave an empty draft behind. Creating by name
      // returns the open draft already using it, so a retry after a failed
      // save does not make a second one.
      let draftId = selection?.kind === 'draft' ? selection.id : null;
      if (selection?.kind === 'draft' && draftId === null) {
        draftId = (await createIntakeDraft(selection.label)).id;
      }

      // Always, and first. It is the one thing that can be said about any row,
      // including a transmission whose pages are still downloading or could
      // not be read at all, and a row nobody can act on is what the single
      // list exists to prevent.
      await setClassification(item.kind, item.id, classification(draftId));

      // Only when they actually moved. Opening a row to set its type should
      // not bake a new version of the document, or make a document out of a
      // transmission that nobody asked to change.
      const pagesChanged =
        pages.length > 0 && JSON.stringify(refs(pages)) !== originalPages;

      if (item.kind === 'document') {
        if (pagesChanged) {
          if (item.version === null) {
            // Every document has a version, so a null one means the row and
            // this screen disagree. Defaulting to 1 would either pass by luck
            // or 409 about a version nobody chose, and both hide it.
            onError(
              'That document could not be saved. Please reload and retry.'
            );
            return;
          }
          await editFaxDocumentPages(item.id, refs(pages), item.version);
        }
        // Independent of the pages: renaming does not touch the version, so it
        // cannot be refused by the version check and does not need repeating
        // if the page edit already ran.
        if (wanted !== item.name) {
          await renameFaxDocument(item.id, wanted);
        }
      } else if (pagesChanged) {
        // Changing what arrived makes a document of it. The transmission keeps
        // its own pages untouched, so the fax as sent is still on record, and
        // the server carries the classification onto what it became.
        await assembleFaxDocument({
          name: wanted,
          sources: sourceIds,
          pages: refs(pages),
        });
      } else if (wanted !== (item.name ?? '')) {
        // Naming a transmission in place. It is the one thing you can do to
        // what arrived that leaves it exactly as it arrived.
        await renameFaxItem({ kind: item.kind, id: item.id }, wanted);
      }
      onSaved();
      onClose();
    } catch (error) {
      // 409 is the server refusing a page list built against an older version,
      // which is a different thing to tell somebody than a failure: their work
      // is not wrong, it is aimed at a document that has moved.
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (getErrorConflictCode(error) === 'FAX_NOT_ROUTABLE') {
        onError(
          getBusinessErrorMessage(error) ?? 'Could not save those changes.'
        );
      } else if (status === 409) {
        onConflict();
        onClose();
      } else {
        onError('Could not save those changes.');
      }
    } finally {
      setSaving(false);
    }
  }, [
    classification,
    item,
    name,
    selection,
    originalPages,
    onClose,
    onConflict,
    onError,
    onSaved,
    pages,
    sourceIds,
  ]);

  // Filing copies what the server holds, so what is on screen must be saved.
  const unsaved =
    !!item &&
    (name !== (item.name ?? '') ||
      typeId !== (item.fax_type_id ?? NONE) ||
      (selection?.id ?? null) !==
        (item.patient_id ?? item.intake_draft_id ?? null) ||
      admissionId !== (item.admission_id ?? NONE) ||
      tags
        .map(tag => tag.id)
        .sort()
        .join() !==
        (item.tags ?? [])
          .map(tag => tag.id)
          .sort()
          .join() ||
      (pages.length > 0 && JSON.stringify(refs(pages)) !== originalPages));

  // Its pages and patient are what the chart holds, so they stay as filed.
  const filed = !!item?.chart_filed_at;

  const toggleArchive = async () => {
    if (!item) return;
    setArchiving(true);
    try {
      if (item.archived_at) {
        await unarchiveFaxItem(item.kind, item.id);
        onRouted('Moved back to the inbox.');
      } else {
        await archiveFaxItem(item.kind, item.id);
        onRouted('Archived.');
      }
    } catch (error) {
      console.error('Failed to archive the fax', error);
      onError('Could not archive this fax.');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <Dialog open={item !== null} onOpenChange={open => !open && onClose()}>
      {/* Capped and column-laid-out so the pages scroll inside the dialog and
          the buttons stay put. A page preview is as tall as a page, which on a
          laptop is taller than the window: without this the action row is
          pushed below the fold and Save cannot be reached at all. */}
      <DialogContent className='flex max-h-[90vh] max-w-6xl flex-col'>
        <DialogHeader>
          <DialogTitle>
            {item?.kind === 'document' ? 'Edit document' : 'Edit fax'}
          </DialogTitle>
        </DialogHeader>
        <div className='flex min-h-0 flex-1 flex-col gap-4'>
          <div className='flex items-center gap-2'>
            <Input
              aria-label='Name'
              placeholder='What this is'
              value={name}
              onChange={event => setName(event.target.value)}
              disabled={saving}
            />
            {/* The rail shows a page at a time, which is the right shape for
                rearranging and the wrong one for reading a long document
                through. This is the way back to the whole thing as it was
                stored, without closing what is half edited. */}
            <Button
              variant='outline'
              type='button'
              disabled={!item}
              onClick={() => void openOriginal()}
            >
              <ExternalLink className='mr-2 h-4 w-4' />
              Open original
            </Button>
          </div>
          {item && (
            <div className='flex items-start gap-2 text-sm'>
              <span className='text-muted-foreground'>Sent by</span>
              <SenderLabel item={item} />
            </div>
          )}
          {/* A signed order that came back knows which order it answers,
              because the mark on it said so. Linking it saves hunting for
              that order by patient and date. */}
          {item?.answers_order_id && (
            <div className='flex flex-wrap items-center gap-2 text-sm text-muted-foreground'>
              <span>
                {item.kind === 'document' ? 'Part of' : 'Answers'}{' '}
                <Link
                  className='font-medium text-primary underline underline-offset-2'
                  to={ordersHighlightPath(
                    item.answers_order_id,
                    // Named from the order's own status rather than assumed.
                    // Answering the question moves it to another tab, and a
                    // link fixed on one would stop finding it the moment it
                    // was used for what it is for.
                    item.answers_order_status
                      ? orderManagerTabForStatus(item.answers_order_status)
                      : null
                  )}
                >
                  Order #{item.answers_order_id.slice(0, 8)}
                </Link>
                .
              </span>
              {/* Asked here because this is the one screen showing the page.
                  A mark on a fax says which order it belongs to and nothing
                  about whether anybody signed it, and nobody can answer that
                  from a row in a list. */}
              {item.kind === 'arrival' &&
                item.answers_order_awaiting_decision && (
                  <span className='flex items-center gap-2'>
                    <span>Is it signed?</span>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      disabled={deciding}
                      onClick={() => void decideSignature(false)}
                    >
                      Not signed
                    </Button>
                    <Button
                      type='button'
                      size='sm'
                      disabled={deciding}
                      onClick={() => void decideSignature(true)}
                    >
                      Confirm signed
                    </Button>
                  </span>
                )}
              {/* Already on its order, so it needs taking out of the list,
                  not filing. */}
              {!item.chart_filed_at && (
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  disabled={archiving || saving}
                  onClick={() => void toggleArchive()}
                >
                  {item.archived_at ? 'Move back to inbox' : 'Archive'}
                </Button>
              )}
            </div>
          )}
          {/* What this fax is, above its pages: somebody opening a row is
              usually answering that before they rearrange anything, and on a
              transmission that is still downloading it is the only thing they
              can answer. */}
          <div className='grid shrink-0 gap-4 sm:grid-cols-2'>
            <div className='space-y-1.5'>
              <Label htmlFor='fax-type'>Type</Label>
              <Select
                value={typeId}
                onValueChange={setTypeId}
                disabled={saving}
              >
                <SelectTrigger id='fax-type' aria-label='Type'>
                  <SelectValue placeholder='Select' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {typeOptions.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <PatientOrDraftPicker
              selected={selection}
              onChoosePatient={choosePatient}
              onChooseDraft={chooseDraft}
              onStartDraft={startDraft}
              onClear={clearSelection}
              disabled={saving || filed}
              // Saving is what writes the draft and the link together, and
              // Save needs a name, so without one the draft could only be
              // created and then stranded.
              startDraftDisabledReason={
                name.trim()
                  ? null
                  : 'Name this fax first, so the draft can save with it.'
              }
              onError={onError}
            />

            <div className='space-y-1.5'>
              <Label>Tags</Label>
              <TagPicker
                selected={tags}
                onChange={setTags}
                disabled={saving}
                onError={onError}
              />
            </div>

            {/* Only with a patient: an admission on its own says nothing about
                whose fax this is, and the server refuses one anyway. A draft
                has none, because the patient it will become does not exist. */}
            {selection?.kind === 'patient' && (
              <div className='space-y-1.5'>
                <Label htmlFor='fax-admission'>Admission</Label>
                <Select
                  value={admissionId}
                  onValueChange={setAdmissionId}
                  disabled={saving || filed || admissions.length === 0}
                >
                  <SelectTrigger id='fax-admission' aria-label='Admission'>
                    <SelectValue
                      placeholder={
                        admissions.length === 0
                          ? 'No admissions'
                          : 'Select admission'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>None</SelectItem>
                    {admissions.map(admission => (
                      <SelectItem key={admission.id} value={admission.id}>
                        {admission.soc_date}
                        {admission.discharge_date
                          ? ` to ${admission.discharge_date}`
                          : ' (open)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {item?.kind === 'arrival' && item.status === 'processing' && (
            <p className='shrink-0 text-sm text-muted-foreground'>
              Still arriving. Close and reopen once it is ready to see every
              page.
            </p>
          )}
          {preparing ? (
            <div className='flex justify-center py-10'>
              <Loader2 className='h-6 w-6 animate-spin' />
            </div>
          ) : (
            <>
              {splitKeys.size > 0 && (
                <div className='flex shrink-0 flex-col gap-2 rounded border bg-muted/40 p-3 sm:flex-row sm:items-center'>
                  <span className='whitespace-nowrap text-sm'>
                    {taken.length === 1
                      ? '1 page selected'
                      : `${taken.length} pages selected`}
                  </span>
                  <Input
                    aria-label='Name for the new fax'
                    placeholder='What the new fax is'
                    className='sm:flex-1'
                    value={splitName}
                    disabled={saving}
                    onChange={event => setSplitName(event.target.value)}
                  />
                  <Button
                    variant='outline'
                    className='whitespace-nowrap'
                    disabled={saving || !canSplit}
                    onClick={() => void split()}
                  >
                    <Scissors className='mr-2 h-4 w-4' />
                    Split into a new fax
                  </Button>
                </div>
              )}
              {kept.length === 0 && splitKeys.size > 0 && (
                <p className='shrink-0 text-sm text-muted-foreground'>
                  Leave at least one page behind, or there is nothing left to
                  split from.
                </p>
              )}
              <div className='min-h-0 flex-1 overflow-y-auto'>
                <PageEditor
                  sources={sources}
                  pages={pages}
                  onChange={setPages}
                  onSourceLoaded={onSourceLoaded}
                  disabled={saving || filed}
                  splitKeys={splitKeys}
                  onToggleSplit={toggleSplit}
                />
              </div>
            </>
          )}
          <div className='flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center'>
            <div className='flex flex-1 flex-wrap items-center gap-2 text-sm'>
              {item?.chart_filed_at ? (
                <span>
                  {item.patient_id && item.chart_task_id ? (
                    <>
                      Filed to{' '}
                      <Link
                        className='font-medium text-primary underline underline-offset-2'
                        to={`/patients/${item.patient_id}/visits/${item.chart_task_id}`}
                      >
                        {item.chart_task_label ?? 'its task'}
                      </Link>
                    </>
                  ) : (
                    'Filed to the chart'
                  )}
                </span>
              ) : (
                <>
                  <Button
                    variant='outline'
                    type='button'
                    onClick={() => setFilingOpen(true)}
                    disabled={
                      saving ||
                      unsaved ||
                      !item?.patient_id ||
                      !!item?.archived_at
                    }
                  >
                    File to chart
                  </Button>
                  {item && !item.patient_id && (
                    <span className='text-muted-foreground'>
                      Choose a patient and save first.
                    </span>
                  )}
                  {item?.patient_id && unsaved && (
                    <span className='text-muted-foreground'>Save first.</span>
                  )}
                </>
              )}
            </div>
            <div className='flex justify-end gap-2'>
              <Button variant='outline' onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button
                onClick={() => void save()}
                // Only the name gates this. A transmission whose pages cannot
                // be read still has a type, a patient and tags worth saving.
                disabled={saving || !name.trim()}
              >
                {saving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                Save
              </Button>
            </div>
          </div>
        </div>
        <FileToChartDialog
          item={filingOpen ? item : null}
          onClose={() => setFilingOpen(false)}
          onError={onError}
          onFiled={result => {
            setFilingOpen(false);
            onRouted(
              result.already_filed
                ? `Already filed to ${result.task_display_name}.`
                : `Filed to ${result.task_display_name}.`
            );
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
